import { formatDateOnly } from "@helpers/formatDate";

/** Перевіряє, чи напрям позиції є long/buy. */
const isLongDirection = (side) => {
  const s = String(side ?? "").toLowerCase();
  return s === "buy" || s === "long";
};

/** Форматує quantity з керованою кількістю знаків після крапки. */
export const displayQuantity = (order, options = {}) => {
  if (order.quantity != null && order.quantity !== "") {
    const n = Number(order.quantity);
    if (!Number.isNaN(n)) {
      const fixed = options.fractionDigits;
      if (fixed != null && fixed !== "") {
        const d = Math.min(20, Math.max(0, Number(fixed) || 0));
        return n.toLocaleString(undefined, {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        });
      }
      const abs = Math.abs(n);
      const decimals = abs >= 1 ? 4 : 6;
      return n.toLocaleString(undefined, {
        maximumFractionDigits: decimals,
        minimumFractionDigits: 0,
      });
    }
    return String(order.quantity);
  }
  return "—";
};

/** Повертає realized PnL як число або fallback-рядок. */
const displayRealizedPnl = (order) => {
  const v = order.realized_pnl ?? order.pnl;
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isNaN(n) ? String(v) : n;
};

/** Дістає числовий realized PnL з даних ордера. */
const getRealizedPnlNumeric = (order) => {
  const v = order.realized_pnl ?? order.pnl;
  if (v == null || v === "") return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isNaN(n) ? null : n;
};

/** Форматує головний рядок realized PnL у USDT. */
const formatRealizedUsdtLine = (pnlNum) => {
  if (pnlNum == null || Number.isNaN(pnlNum)) return "—";
  return `${formatNormalizedPrice(pnlNum, "—")} USDT`;
};

/** Обчислює й форматує ROI для history-рядка. */
const formatRealizedRoiLine = (order, pnlNum) => {
  const margin = Number(order.margin);
  if (
    pnlNum == null ||
    Number.isNaN(pnlNum) ||
    Number.isNaN(margin) ||
    margin === 0
  ) {
    return null;
  }
  const roi = (pnlNum / margin) * 100;
  return `(${roi.toFixed(2)}%)`;
};

/** Форматує еквівалент realized PnL у USD. */
const formatRealizedUsdLine = (pnlNum) => {
  if (pnlNum == null || Number.isNaN(pnlNum)) return null;
  return `=${formatNormalizedPrice(pnlNum, "—")} USD`;
};

const parseNumeric = (value) => {
  if (value == null || value === "") return null;
  const normalized = String(value).trim().replace(/\s/g, "").replace(/,/g, "");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

/** Нормалізує символ пари до alphanumeric-формату (BTCUSDT). */
export const normalizeOrderSymbol = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

/** Порівнює символи ордера та поточної пари незалежно від формату. */
export const symbolsMatch = (orderSymbol, pairSlug) =>
  normalizeOrderSymbol(orderSymbol) === normalizeOrderSymbol(pairSlug);

/** Повертає символ для відображення у таблиці history. */
const displaySymbol = (order) => {
  const raw = String(order.symbol ?? "").trim();
  if (!raw) return "—";
  return raw.includes("/") ? raw : raw.toUpperCase();
};

/** Визначає базовий актив із символу торгової пари. */
const getBaseAsset = (order) => {
  const raw = normalizeOrderSymbol(order.symbol);
  if (!raw) return "";
  const knownQuotes = ["USDT", "USDC", "BUSD", "USD", "BTC", "ETH"];
  for (const quote of knownQuotes) {
    if (raw.endsWith(quote) && raw.length > quote.length) {
      return raw.slice(0, -quote.length);
    }
  }
  return raw.includes("/") ? raw.split("/")[0] : raw;
};

/** Повертає час відкриття позиції з доступних полів. */
const getOpenTime = (order) =>
  order.created_at ?? order.opened_at ?? order.order_time ?? "—";

/** Повертає час закриття позиції з доступних полів. */
const getCloseTime = (order) => order.closed_at ?? order.close_time ?? "—";

/** Нормалізує статус позиції для відображення. */
const displayPositionStatus = (order, __) => {
  const raw = String(order.status ?? "").toLowerCase();
  if (raw === "closed" || raw === "close" || raw === "") {
    return __("futures.closed");
  }
  return String(order.status);
};

/** Додає "/" у символ торгової пари для відображення в UI. */
export const addSlashToSymbol = (value) => {
  const raw = String(value ?? "")
    .toUpperCase()
    .replace("/", "");

  if (!raw) return "";

  const knownQuotes = ["USDT", "USDC", "BUSD", "USD", "BTC", "ETH"];

  for (const quote of knownQuotes) {
    if (raw.endsWith(quote) && raw.length > quote.length) {
      const base = raw.slice(0, -quote.length);
      return `${base}/${quote}`;
    }
  }

  return raw;
};

/** Нормалізує side та повертає прапорці buy/sell. */
export const normalizeOrderSide = (side) => {
  const s = String(side ?? "").toLowerCase();
  const isBuySide = s === "buy" || s === "long";
  const isSellSide = s === "sell" || s === "short";
  return { isBuySide, isSellSide };
};

/** Текст напряму для історії ордерів. */
export const getDirectionText = (side, __) => {
  const { isBuySide } = normalizeOrderSide(side);
  if (typeof __ === "function") {
    return isBuySide ? __("futures.open-long") : __("futures.open-short");
  }
  return isBuySide ? "Open Long" : "Open Short";
};

/** Колір напряму для таблиць ордерів. */
export const getDirectionColorClass = (side) => {
  const { isBuySide } = normalizeOrderSide(side);
  return isBuySide
    ? "futures-orders-table__value--green"
    : "futures-orders-table__value--red";
};

/** Дата з API: ISO 8601 в UTC або старий "DD/MM/YYYY HH:mm:ss" (new Date його не розбирає). */
export const parseOrderDate = (value) => {
  if (value == null || value === "") return null;

  const str = String(value).trim();
  const dmy = str.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );
  const date = dmy
    ? new Date(
        Number(dmy[3]),
        Number(dmy[2]) - 1,
        Number(dmy[1]),
        Number(dmy[4] ?? 0),
        Number(dmy[5] ?? 0),
        Number(dmy[6] ?? 0),
      )
    : new Date(str);

  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Apr 17, 2026" (month "short") / "April 17, 2026" (month "long"), null якщо дати немає. */
export const formatOrderDate = (value, month = "short") =>
  parseOrderDate(value)?.toLocaleDateString("en-US", {
    month,
    day: "numeric",
    year: "numeric",
  }) ?? null;

/** Обчислює поточні PnL/ROI для відкритої позиції. */
export const getPositionPnlRoi = (order, markPrice) => {
  const quantity =
    parseNumeric(order?.quantity) ??
    parseNumeric(order?.qty) ??
    parseNumeric(order?.size) ??
    0;
  const quantityAbs = Math.abs(quantity);
  const entryPrice =
    parseNumeric(order?.entry_price) ??
    parseNumeric(order?.fill_price) ??
    parseNumeric(order?.open_price) ??
    parseNumeric(order?.price) ??
    0;
  const currentPrice =
    parseNumeric(markPrice) ??
    parseNumeric(order?.mark_price) ??
    parseNumeric(order?.current_price) ??
    parseNumeric(order?.last_price) ??
    null;
  const hasLiveMarkPrice = Number.isFinite(currentPrice) && currentPrice > 0;
  const fallbackPnl =
    parseNumeric(order?.unrealized_pnl) ?? parseNumeric(order?.pnl) ?? 0;
  const fallbackRoi =
    parseNumeric(order?.unrealized_roi) ?? parseNumeric(order?.roi) ?? 0;
  const { isSellSide } = normalizeOrderSide(order?.side);
  const pnlValue = hasLiveMarkPrice
    ? isSellSide
      ? (entryPrice - currentPrice) * quantityAbs
      : (currentPrice - entryPrice) * quantityAbs
    : fallbackPnl;
  const margin =
    parseNumeric(order?.margin) ??
    parseNumeric(order?.cost) ??
    parseNumeric(order?.amount) ??
    0;
  const roiValue =
    hasLiveMarkPrice && margin > 0 ? (pnlValue / margin) * 100 : fallbackRoi;
  const hasValidCalc =
    hasLiveMarkPrice &&
    Number.isFinite(entryPrice) &&
    entryPrice > 0 &&
    quantityAbs > 0;

  return {
    quantity,
    pnlValue: hasValidCalc ? pnlValue : null,
    roiValue: hasValidCalc && Number.isFinite(roiValue) ? roiValue : null,
    pnlText: hasValidCalc ? pnlValue.toFixed(2) : "--",
    roiText:
      hasValidCalc && Number.isFinite(roiValue) ? roiValue.toFixed(2) : "--",
    ...normalizeOrderSide(order?.side),
  };
};

/** Той самий PnL, який показує стовпець Profit/Loss відкритого трейду. */
export const getDisplayedPositionPnl = (order, markPrice) => {
  const { pnlValue } = getPositionPnlRoi(order, markPrice);

  if (pnlValue != null && Number.isFinite(pnlValue)) return pnlValue;

  if (order?.pnl == null || order?.pnl === "") return null;

  const fallbackPnl = Number(String(order.pnl).replace(/,/g, "").trim());

  return Number.isFinite(fallbackPnl) ? fallbackPnl : null;
};

/** Mark price позиції в тому ж порядку, що й таблиця відкритих трейдів. */
export const resolvePositionMarkPrice = (
  order,
  {
    markPrices = {},
    pairSlug,
    activePairMarkPrice,
    lastPrice,
    tickersMap,
  } = {},
) => {
  const symbol = normalizeOrderSymbol(
    order?.symbol ?? order?.pair ?? order?.pair_name ?? order?.slug ?? "",
  );

  if (!symbol) return null;

  const fromGuideMock = Number(order?.mark_price ?? order?.markPrice);

  if (
    order?.isGuideMock &&
    Number.isFinite(fromGuideMock) &&
    fromGuideMock > 0
  ) {
    return fromGuideMock;
  }

  const fromRowWs = markPrices?.[symbol];
  if (Number.isFinite(fromRowWs) && fromRowWs > 0) return fromRowWs;

  if (symbolsMatch(symbol, pairSlug)) {
    const fromActiveMark = Number(activePairMarkPrice);
    if (Number.isFinite(fromActiveMark) && fromActiveMark > 0) {
      return fromActiveMark;
    }

    const fromActiveLast = Number(lastPrice);
    if (Number.isFinite(fromActiveLast) && fromActiveLast > 0) {
      return fromActiveLast;
    }
  }

  const fromCacheLast = Number(tickersMap?.[symbol.toLowerCase()]?.lastPrice);
  if (Number.isFinite(fromCacheLast) && fromCacheLast > 0) return fromCacheLast;

  return null;
};

export const sumDisplayedPositionPnl = (orders, resolveMarkPrice) => {
  if (!Array.isArray(orders) || typeof resolveMarkPrice !== "function") return 0;

  return orders.reduce((sum, order) => {
    const livePnl = getDisplayedPositionPnl(order, resolveMarkPrice(order));

    return sum + (livePnl != null && Number.isFinite(livePnl) ? livePnl : 0);
  }, 0);
};

/** Нормалізує ціну: пробіл між тисячами та адаптивні дроби. */
export const formatNormalizedPrice = (value, emptyValue = "--") => {
  if (value == null || value === "") return emptyValue;

  let normalized = String(value).trim().replace(/\s/g, "");
  if (!normalized) return emptyValue;

  if (normalized.includes(".")) {
    normalized = normalized.replace(/,/g, "");
  } else if (normalized.includes(",")) {
    const commasCount = (normalized.match(/,/g) || []).length;
    normalized =
      commasCount === 1
        ? normalized.replace(",", ".")
        : normalized.replace(/,/g, "");
  }

  const numericValue = Number(normalized);
  if (Number.isNaN(numericValue)) return String(value);

  const fractionDigits = Math.abs(numericValue) < 1000 ? 2 : 1;
  const [integerPart, decimalPart] = numericValue
    .toFixed(fractionDigits)
    .split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${formattedInteger}.${decimalPart}`;
};

/** Будує уніфіковану модель рядка для history (desktop/mobile). */
export const buildPositionHistoryRowModel = (
  order,
  __,
  { shortDates = false, quantityFractionDigits } = {},
) => {
  const long = isLongDirection(order.side);
  const realized = displayRealizedPnl(order);
  const realizedNum = getRealizedPnlNumeric(order);
  const hasRealizedNum = realizedNum != null && !Number.isNaN(realizedNum);
  const realizedMainLine = hasRealizedNum
    ? formatRealizedUsdtLine(realizedNum)
    : typeof realized === "number"
    ? formatRealizedUsdtLine(realized)
    : String(realized ?? "—");
  const roiLine = hasRealizedNum && formatRealizedRoiLine(order, realizedNum);
  const usdLine = hasRealizedNum && formatRealizedUsdLine(realizedNum);
  const openRaw = getOpenTime(order);
  const closeRaw = getCloseTime(order);
  const formatTimeCell = (raw) => {
    if (raw == null || raw === "" || raw === "—") return "—";
    return shortDates ? formatDateOnly(raw) || raw : raw;
  };
  const quantityNum = Number(order.quantity);
  const hasQuantityNum = !Number.isNaN(quantityNum);
  const baseAsset = getBaseAsset(order);
  const quantityColorClass =
    hasQuantityNum && quantityNum !== 0
      ? quantityNum < 0
        ? "futures-orders-table__value--red"
        : "futures-orders-table__value--green"
      : "";
  const pnlColorClass =
    hasRealizedNum && realizedNum < 0
      ? "futures-orders-table__value--red"
      : hasRealizedNum && realizedNum > 0
      ? "futures-orders-table__value--green"
      : "";

  return {
    long,
    realizedMainLine,
    roiLine,
    usdLine,
    hasRealizedNum,
    closePrice: formatNormalizedPrice(
      order.closed_price ?? order.close_price,
      "—",
    ),
    entryPrice: formatNormalizedPrice(order.entry_price, "—"),
    openTime: formatTimeCell(openRaw),
    closeTime: formatTimeCell(closeRaw),
    symbol: displaySymbol(order),
    baseAsset,
    quantityText: `${displayQuantity(
      order,
      quantityFractionDigits != null
        ? { fractionDigits: quantityFractionDigits }
        : {},
    )}${baseAsset ? ` ${baseAsset}` : ""}`,
    quantityColorClass,
    pnlColorClass,
    statusLabel: displayPositionStatus(order, __),
  };
};

export const formatOrderQuantity = (value) => {
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "") : value;

  const number = Number(normalizedValue);

  if (!Number.isFinite(number)) return "0";

  if (number === 0) return "0.0";

  const integerPart = Math.trunc(Math.abs(number)).toString();
  const integerDigits = integerPart.length;

  let fractionDigits;

  if (integerDigits <= 1) {
    fractionDigits = 4;
  } else if (integerDigits === 2) {
    fractionDigits = 3;
  } else {
    fractionDigits = 2;
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(number);
};

export const formatCompactOrderTime = (value) => {
  if (!value) return "--";

  const stringValue = String(value).trim();

  const dayMonthYearMatch = stringValue.match(
    /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/,
  );

  if (dayMonthYearMatch) {
    const [, day, month, , hours, minutes] = dayMonthYearMatch;

    return `${day}.${month} ${hours}:${minutes}`;
  }

  const yearMonthDayMatch = stringValue.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/,
  );

  if (yearMonthDayMatch) {
    const [, , month, day, hours, minutes] = yearMonthDayMatch;

    return `${day}.${month} ${hours}:${minutes}`;
  }

  return stringValue;
};

export const formatUsdAmount = (value, emptyValue = "0") => {
  if (value == null || value === "") return emptyValue;

  const normalizedValue = String(value)
    .trim()
    .replace(/\s/g, "")
    .replace(/,/g, "");
  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue)) return emptyValue;

  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: numericValue === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericValue)}`;
};
